import { computed, type Ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { GridFieldType, TableDynamicColumn } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import {
  createEmptyObjectPassportElement,
  mergeLoadedObjectPassportElement,
  type ObjectPassportElement,
} from '@/features/Inventory/ObjectListEdit/model/model'
import { formatLocalWallClockVerDateNow, useHttp } from '@/shared/lib'
import { resolvedTableSegments, type ResolvedTableSegment } from './runtimeTableLayout'
import { resolveRuntimeTableColumnDisplay } from './resolveLinkedPassportValues'
import {
  buildTableBoundPassportsSearchParams,
  parseTableBoundPassportsResponse,
} from './tableBoundPassportsApi'
import {
  buildRowsFromPassportElements,
  type PassportRuntimeListTableRow,
} from './runtimeListTableRows'
import { PASSPORT_RUNTIME_LIST_TABLE_QUERY_KEY } from './passportRuntimeListTableQueryKeys'
import { coordsPresent } from '@/features/Inventory/PlaceOnMap'
import { resolveListGroupListInventTypeId } from './resolveListGroupListInventTypeId'
import { extractLinkedObjectIdsFromByType } from './passportListGroupStats'

export type { PassportRuntimeListTableRow }

export { PASSPORT_RUNTIME_LIST_TABLE_QUERY_KEY } from './passportRuntimeListTableQueryKeys'

type RuntimeFilter = { status_date: string | null }

type ListTableQueryPayload = {
  rows: PassportRuntimeListTableRow[]
  summary: Record<string, { value: string; unit?: string }>
}

function parseRuntimeListTableSummarySlice(res: { data?: unknown }): {
  summary: Record<string, { value: unknown; unit?: string }>
} {
  const root = res.data
  if (root == null || typeof root !== 'object') return { summary: {} }
  const payload =
    'data' in root && (root as { data: unknown }).data != null && typeof (root as { data: unknown }).data === 'object'
      ? (root as { data: Record<string, unknown> }).data
      : (root as Record<string, unknown>)
  const summaryRaw = payload.summary
  const summary =
    summaryRaw && typeof summaryRaw === 'object' && !Array.isArray(summaryRaw)
      ? (summaryRaw as Record<string, { value: unknown; unit?: string }>)
      : {}
  return { summary }
}

function normalizeSummaryMap(
  raw: Record<string, { value: unknown; unit?: string }>,
): Record<string, { value: string; unit?: string }> {
  return Object.fromEntries(
    Object.entries(raw).map(([k, v]) => {
      const value = v?.value == null ? '' : String(v.value)
      const unit = v?.unit != null && String(v.unit).trim() !== '' ? String(v.unit).trim() : undefined
      return [k, { value, unit }]
    }),
  )
}

function formatSummaryValue(entry: { value: string; unit?: string } | undefined): string {
  if (!entry) return ''
  const u = entry.unit?.trim()
  return u ? `${entry.value} ${u}`.trim() : entry.value
}

export function usePassportRuntimeListTable(options: {
  layoutItem: Ref<GridFieldType>
  /** Map group id for URL path `/inventory/objects/{this}/passport/...` (route param `object_id` on passport v2 page). */
  inventoryGroupObjectId: Ref<string>
  /** Parent list row `object_list_id` for query `object_id` (route param `invent_object_id`). */
  passportObjectListId: Ref<string>
  filter: Ref<RuntimeFilter>
  allFields: Ref<ObjectInventoryField[]>
  /** Passport API `stats` — fallback source for list rows via `by_type` subtypes. */
  stats?: Ref<unknown>
  /** Object-type LIST/tab groups — used to resolve `inventory_type` when grid omits it. */
  fieldGroups?: Ref<readonly unknown[]>
}) {
  const http = useHttp()

  const listGroupId = computed(() =>
    options.layoutItem.value.list_group_id != null ? Number(options.layoutItem.value.list_group_id) : null,
  )

  const segments = computed(() => resolvedTableSegments(options.layoutItem.value))

  const columnSegments = computed(() =>
    segments.value.filter((s): s is Extract<ResolvedTableSegment, { kind: 'column' }> => s.kind === 'column'),
  )

  const hasSummaryLayout = computed(() => (options.layoutItem.value.summary_layout?.length ?? 0) > 0)

  const tableQuery = useQuery({
    queryKey: computed(() => [
      PASSPORT_RUNTIME_LIST_TABLE_QUERY_KEY,
      options.inventoryGroupObjectId.value,
      options.passportObjectListId.value,
      String(options.layoutItem.value.id),
      listGroupId.value,
      options.filter.value.status_date,
    ]),
    queryFn: async (): Promise<ListTableQueryPayload> => {
      const layout = options.layoutItem.value
      if (layout.type !== 'table') {
        return { rows: [], summary: {} }
      }

      const cols = columnSegments.value.map((s) => s.column)
      const includeSummary = (layout.summary_layout?.length ?? 0) > 0

      const gid = options.inventoryGroupObjectId.value.trim()
      const bulkParams = buildTableBoundPassportsSearchParams({
        objectId: options.passportObjectListId.value,
        tableFieldId: String(layout.id),
        statusDate: options.filter.value.status_date,
        listGroupId: listGroupId.value,
      })
      const bulkUrl = `/inventory/objects/${encodeURIComponent(gid)}/passport/table-bound-passports?${bulkParams.toString()}`

      let bulkRes: { data?: unknown }
      let summaryNormalized: Record<string, { value: string; unit?: string }> = {}

      if (includeSummary) {
        const rltParams = new URLSearchParams()
        rltParams.set('object_id', String(options.passportObjectListId.value))
        rltParams.set('table_field_id', String(layout.id))
        if (listGroupId.value != null && listGroupId.value > 0) {
          rltParams.set('list_group_id', String(listGroupId.value))
        }
        if (options.filter.value.status_date) {
          rltParams.set('status_date', options.filter.value.status_date)
        } else {
          rltParams.set('client_now', formatLocalWallClockVerDateNow())
        }
        rltParams.set('include_summary', 'true')
        const rltUrl = `/inventory/objects/${encodeURIComponent(gid)}/passport/runtime-list-table?${rltParams.toString()}`

        const [bulk, rlt] = await Promise.all([http.get(bulkUrl), http.get(rltUrl)])
        bulkRes = bulk
        const { summary: rawSummary } = parseRuntimeListTableSummarySlice(rlt)
        summaryNormalized = normalizeSummaryMap(rawSummary)
      } else {
        bulkRes = await http.get(bulkUrl)
      }

      const { data: rawList } = parseTableBoundPassportsResponse(bulkRes.data)
      const seenIds = new Set<number>(
        rawList.map((raw: any) => Number(raw?.id)).filter((id: number) => id > 0),
      )

      const resolvedInventoryType = (() => {
        const n = Number(layout.inventory_type ?? 0)
        if (Number.isFinite(n) && n > 0) return n
        return resolveListGroupListInventTypeId(listGroupId.value, options.fieldGroups?.value)
      })()

      const statsIds = extractLinkedObjectIdsFromByType(options.stats?.value, resolvedInventoryType)
      if (statsIds.length > 0) {
        const missingIds = statsIds.filter((id) => !seenIds.has(id))
        if (missingIds.length > 0) {
          const params = new URLSearchParams()
          params.set('object_ids', missingIds.join(','))
          if (options.filter.value.status_date) {
            params.set('status_date', options.filter.value.status_date)
          } else {
            params.set('client_now', formatLocalWallClockVerDateNow())
          }
          const statsUrl = `/inventory/objects/passport?${params.toString()}`
          const statsRes = await http.get(statsUrl)
          const { data: statsData } = parseTableBoundPassportsResponse(statsRes.data)
          rawList.push(...statsData)
        }
      }

      const mergedElements = rawList.map((raw) =>
        mergeLoadedObjectPassportElement(
          createEmptyObjectPassportElement(),
          raw as Partial<ObjectPassportElement>,
        ),
      )

      const rows = await buildRowsFromPassportElements(
        http,
        options.filter.value,
        options.passportObjectListId.value,
        mergedElements,
        cols,
        options.allFields.value,
        resolvedInventoryType,
        resolveRuntimeTableColumnDisplay,
      )

      return { rows, summary: summaryNormalized }
    },
    enabled: computed(
      () =>
        options.layoutItem.value.type === 'table' &&
        Boolean(options.inventoryGroupObjectId.value) &&
        Boolean(options.passportObjectListId.value) &&
        Boolean(String(options.layoutItem.value.id ?? '').length),
    ),
    retry: false,
  })

  const rows = computed(() => tableQuery.data.value?.rows ?? [])

  const isLoading = computed(() => tableQuery.isLoading.value || tableQuery.isFetching.value)

  const isError = computed(() => tableQuery.isError.value)
  const error = computed(() => tableQuery.error.value ?? null)

  const refetch = () => tableQuery.refetch()

  const summaryValues = computed(() => {
    const batch = tableQuery.data.value?.summary ?? {}
    return Object.fromEntries(Object.entries(batch).map(([k, v]) => [k, formatSummaryValue(v)]))
  })

  const summaryLoading = computed(
    () => hasSummaryLayout.value && (tableQuery.isLoading.value || tableQuery.isFetching.value),
  )

  const summaryError = computed(() => (hasSummaryLayout.value ? tableQuery.error.value : null))

  const refetchSummary = () => tableQuery.refetch()

  /** Summary strip uses `runtime-list-table` when `summary_layout` is configured; rows always come from bulk `table-bound-passports`. */
  const summaryApiEnabled = computed(() => hasSummaryLayout.value)

  return {
    rows,
    isLoading,
    isError,
    error,
    refetch,
    summaryValues,
    summaryLoading,
    summaryError,
    refetchSummary,
    summaryApiEnabled,
    coordsPresent,
  }
}
